import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaSun, FaWind, FaWater, FaLeaf } from 'react-icons/fa';

const RenewableEnergyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Renewable Energy' }
  ];

  const renewableTypes = [
    {
      icon: <FaSun className="w-10 h-10 text-yellow-500" />,
      title: "Solar Energy",
      description: "Investments in utility-scale solar installations, residential solar projects, and emerging photovoltaic technologies."
    },
    {
      icon: <FaWind className="w-10 h-10 text-blue-400" />,
      title: "Wind Power",
      description: "Strategic positions in onshore and offshore wind farms, with a focus on next-generation turbine technology."
    },
    {
      icon: <FaWater className="w-10 h-10 text-blue-600" />,
      title: "Hydroelectric",
      description: "Funding for sustainable hydroelectric projects that balance energy production with ecological considerations."
    },
    {
      icon: <FaLeaf className="w-10 h-10 text-green-500" />,
      title: "Biomass Energy",
      description: "Investments in eco-friendly biomass energy solutions that convert organic materials into clean, renewable energy."
    }
  ];

  const investmentApproach = [
    {
      title: "Direct Project Investment",
      description: "Strategic equity positions in renewable energy projects at various development stages, from greenfield to operational assets."
    },
    {
      title: "Infrastructure Partnerships",
      description: "Joint ventures with established developers and operators to accelerate deployment of renewable energy solutions."
    },
    {
      title: "Technology Innovation",
      description: "Targeted investments in companies developing breakthrough renewable energy technologies with commercialization potential."
    },
    {
      title: "ESG-Focused Vehicles",
      description: "Participation in specialized investment vehicles with strict environmental, social, and governance criteria."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Renewable Energy Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Investing in a Sustainable Energy Future
              </h2>
              <p className="text-lg text-gray-700">
                The Segway Capital Renewable Energy Fund provides investors with diversified exposure to the rapidly growing renewable energy sector, combining attractive financial returns with positive environmental impact.
              </p>
              <p className="text-lg text-gray-700">
                Our strategic approach targets investments across the renewable energy value chain, including project development, infrastructure, technology innovation, and operational assets across multiple geographies and energy types.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Explore Investment Opportunities
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/beautiful-view-wind-turbines-grass-covered-field-captured-holland_181624-6013.jpg_er2ofu.jpg" 
                alt="Wind Turbines in Field" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Renewable Focus Areas
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We build a balanced portfolio across multiple renewable energy categories to optimize returns while managing risk through diversification.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {renewableTypes.map((type, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-6 flex justify-center">{type.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3 text-center">{type.title}</h4>
                <p className="text-gray-600 text-center">{type.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/green-energy-full-developmentincrease-electricity-prices-world-market_117255-2248.jpg_zoa2fn.jpg" 
                alt="Solar Panel Installation" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Investment Approach
              </h3>
              <p className="text-lg text-gray-700">
                Our comprehensive investment strategy allows us to capture opportunities across the renewable energy landscape, from early-stage development to established operational assets.
              </p>
              
              <div className="space-y-6">
                {investmentApproach.map((approach, index) => (
                  <div key={index} className="border-l-4 border-secondary pl-4">
                    <h4 className="text-xl font-heading font-semibold mb-2">{approach.title}</h4>
                    <p className="text-gray-600">{approach.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$120M+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">15</div>
                <div className="text-gray-300">Countries</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">35+</div>
                <div className="text-gray-300">Projects Funded</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">18.3%</div>
                <div className="text-gray-300">Average Annual Return</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RenewableEnergyPage;
